/**
 * Resolve effective Catchball routing policy for a cycle/track.
 */

import { useMemo } from 'react';
import { useDegapProcessStore } from '../../stores/prioritization/degapProcessStore';
import { useWorkflowStore } from '../../stores/prioritization/workflowStore';
import { useTrackStore } from '../../stores/prioritization/trackStore';
import {
  getCatchballRoutingInheritanceBreakdown,
  resolveEffectiveCatchballRouting,
} from '../../lib/effectiveCatchballRoutingResolver';
import type {
  CatchballRoutingInheritanceBreakdown,
  EffectiveCatchballRouting,
} from '../../types/prioritization/catchballRouting';

export function useCatchballRouting(
  cycleId: string | undefined | null,
  trackId?: string | null,
): {
  routing: EffectiveCatchballRouting;
  breakdown: CatchballRoutingInheritanceBreakdown;
} {
  const cycle = useDegapProcessStore((s) =>
    cycleId ? s.cycles.find((c) => c.id === cycleId) : undefined,
  );
  const workflows = useWorkflowStore((s) => s.workflows);
  const tracks = useTrackStore((s) => s.tracks);

  return useMemo(() => {
    const workflow =
      (cycle?.workflowId
        ? workflows.find((w) => w.id === cycle.workflowId)
        : undefined) ?? null;
    const track = trackId ? tracks.find((t) => t.id === trackId) : undefined;
    const cycleMetadata = (cycle?.metadata ?? null) as Record<string, unknown> | null;
    const trackMetadata = (track?.metadata ?? null) as Record<string, unknown> | null;

    const input = { workflow, cycleMetadata, trackMetadata };
    return {
      routing: resolveEffectiveCatchballRouting(input),
      breakdown: getCatchballRoutingInheritanceBreakdown(input),
    };
  }, [cycle, trackId, tracks, workflows]);
}
